import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, App, Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import { MessageOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { sellerSupportAPI } from '../utils/sellerApi'
import { connectSellerReverb, disconnectSellerReverb, listenToSellerTicket, type SellerSupportMessageEvent, type SellerSupportStatusChangedEvent } from '../utils/reverb'
import { useSellerAuth } from '../contexts/SellerAuthContext'

type SupportTopic = {
  code: string
  label: string
  description: string
  default_priority: string
  requires_subject: boolean
  eta?: string | null
}

type Ticket = {
  id: number
  subject: string
  status: string
  priority: string
  topic_code?: string | null
  topic_label?: string | null
  auto_priority_reason?: string | null
  auto_reply_message?: string | null
  created_at: string
  messages_count?: number | null
}

type TicketMessage = {
  id: number
  message: string
  is_admin: boolean
  message_type?: string
  author_type?: string
  author_name?: string
  created_at: string
}

type TicketDetail = Ticket & {
  messages: TicketMessage[]
}

const priorityColors: Record<string, string> = {
  low: 'green',
  medium: 'gold',
  high: 'red',
}

const statusColors: Record<string, string> = {
  open: 'blue',
  pending: 'orange',
  resolved: 'green',
  closed: 'default',
}

const SupportPage = () => {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [topics, setTopics] = useState<SupportTopic[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [ticketModalVisible, setTicketModalVisible] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null)
  const [selectedTopicCode, setSelectedTopicCode] = useState<string>('order_management')
  const [createForm] = Form.useForm<{ topic_code: string; subject?: string; message: string }>()
  const [replyForm] = Form.useForm<{ message: string }>()
  const ticketCleanupRef = useRef<(() => void) | null>(null)
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canCreateTicket = hasPermission('support.create')
  const canReplyTicket = hasPermission('support.reply')
  const canCloseTicket = hasPermission('support.close')

  const selectedTopic = useMemo(
    () => topics.find((topic) => topic.code === selectedTopicCode) ?? null,
    [selectedTopicCode, topics],
  )

  const fetchTickets = useCallback(async () => {
    setLoading(true)
    try {
      const response = await sellerSupportAPI.tickets()
      setTickets(response.data.data?.data ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }, [message])

  const bootstrap = useCallback(async () => {
    setLoading(true)
    try {
      const [ticketsResponse, optionsResponse] = await Promise.all([
        sellerSupportAPI.tickets(),
        sellerSupportAPI.options(),
      ])

      setTickets(ticketsResponse.data.data?.data ?? [])
      const nextTopics = optionsResponse.data.data?.topics ?? []
      setTopics(nextTopics)

      const nextDefaultTopic = nextTopics[0]?.code ?? 'order_management'
      setSelectedTopicCode(nextDefaultTopic)
      createForm.setFieldsValue({ topic_code: nextDefaultTopic })
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load support center')
    } finally {
      setLoading(false)
    }
  }, [createForm, message])

  useEffect(() => {
    void bootstrap()
    connectSellerReverb()

    return () => {
      ticketCleanupRef.current?.()
      disconnectSellerReverb()
    }
  }, [bootstrap])

  useEffect(() => {
    ticketCleanupRef.current?.()
    ticketCleanupRef.current = null

    if (!selectedTicket?.id || !ticketModalVisible) {
      return
    }

    ticketCleanupRef.current = listenToSellerTicket(
      selectedTicket.id,
      (event: SellerSupportMessageEvent) => {
        setSelectedTicket((current) => {
          if (!current || current.id !== event.ticket_id) {
            return current
          }

          const nextMessage = event.message
          const alreadyExists = (current.messages ?? []).some((message) => message.id === nextMessage.id)

          if (alreadyExists) {
            return current
          }

          return {
            ...current,
            messages: [...(current.messages ?? []), nextMessage],
          }
        })

        void fetchTickets()
      },
      (event: SellerSupportStatusChangedEvent) => {
        setSelectedTicket((current) => {
          if (!current || current.id !== event.ticket_id) {
            return current
          }

          return {
            ...current,
            status: event.new_status,
          }
        })

        void fetchTickets()
      },
    )

    return () => {
      ticketCleanupRef.current?.()
      ticketCleanupRef.current = null
    }
  }, [selectedTicket?.id, ticketModalVisible, fetchTickets])

  const createTicket = async (values: { topic_code: string; subject?: string; message: string }) => {
    if (!canCreateTicket) {
      message.warning('You are not permitted to create support tickets.')
      return
    }

    setSaving(true)
    try {
      await sellerSupportAPI.createTicket(values)
      message.success('Ticket created successfully')
      setCreateModalVisible(false)
      createForm.resetFields()
      const fallbackTopic = topics[0]?.code ?? 'order_management'
      setSelectedTopicCode(fallbackTopic)
      createForm.setFieldsValue({ topic_code: fallbackTopic })
      await fetchTickets()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create ticket')
    } finally {
      setSaving(false)
    }
  }

  const openTicket = async (ticketId: number) => {
    setSaving(true)
    try {
      const response = await sellerSupportAPI.getTicket(ticketId)
      setSelectedTicket(response.data.data)
      setTicketModalVisible(true)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load ticket details')
    } finally {
      setSaving(false)
    }
  }

  const replyToTicket = async (values: { message: string }) => {
    if (!selectedTicket) {
      return
    }

    if (!canReplyTicket) {
      message.warning('You are not permitted to reply to support tickets.')
      return
    }

    setSaving(true)
    try {
      await sellerSupportAPI.reply(selectedTicket.id, values.message)
      replyForm.resetFields()
      message.success('Reply sent')
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to send reply')
    } finally {
      setSaving(false)
    }
  }

  const closeTicket = async () => {
    if (!selectedTicket) {
      return
    }

    if (!canCloseTicket) {
      message.warning('You are not permitted to close support tickets.')
      return
    }

    setSaving(true)
    try {
      await sellerSupportAPI.closeTicket(selectedTicket.id)
      message.success('Ticket closed')
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to close ticket')
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<Ticket> = [
    { title: 'Issue', dataIndex: 'subject', key: 'subject' },
    {
      title: 'Topic',
      key: 'topic_label',
      render: (_, record) => record.topic_label || 'Other',
    },
    {
      title: 'Priority',
      key: 'priority',
      render: (_, record) => <Tag color={priorityColors[record.priority] ?? 'default'}>{record.priority}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => <Tag color={statusColors[record.status] ?? 'default'}>{record.status}</Tag>,
    },
    {
      title: 'Messages',
      dataIndex: 'messages_count',
      key: 'messages_count',
    },
    {
      title: 'Created',
      key: 'created_at',
      render: (_, record) => new Date(record.created_at).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button icon={<MessageOutlined />} onClick={() => void openTicket(record.id)}>
          Open
        </Button>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Seller Support Center</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>
            Raise structured issues for orders, products, billing, delivery, bugs, and operations.
          </div>
        </div>
        {canCreateTicket ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              const fallbackTopic = topics[0]?.code ?? 'order_management'
              setSelectedTopicCode(fallbackTopic)
              createForm.setFieldsValue({ topic_code: fallbackTopic })
              setCreateModalVisible(true)
            }}
          >
            New Ticket
          </Button>
        ) : null}
      </div>

      <Table columns={columns} dataSource={tickets} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} />

      <Modal title="Create Seller Support Ticket" open={createModalVisible} onCancel={() => setCreateModalVisible(false)} footer={null}>
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ topic_code: selectedTopicCode }}
          onFinish={createTicket}
          onValuesChange={(changedValues) => {
            if (typeof changedValues.topic_code === 'string') {
              setSelectedTopicCode(changedValues.topic_code)
            }
          }}
        >
          <Form.Item name="topic_code" label="Problem type" rules={[{ required: true, message: 'Problem type is required' }]}>
            <Select
              options={topics.map((topic) => ({
                label: topic.label,
                value: topic.code,
              }))}
            />
          </Form.Item>

          {selectedTopic ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              title={selectedTopic.label}
              description={`${selectedTopic.description}${selectedTopic.eta ? ` Expected review ${selectedTopic.eta}.` : ''}`}
            />
          ) : null}

          {selectedTopic?.requires_subject ? (
            <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Subject is required for this problem type' }]}>
              <Input placeholder="Write your own subject" />
            </Form.Item>
          ) : null}

          <Form.Item name="message" label="Details" rules={[{ required: true, message: 'Message is required' }]}>
            <Input.TextArea rows={6} placeholder="Explain what happened, which screen or workflow is affected, and any identifiers like order IDs or invoice numbers." />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setCreateModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Submit Ticket
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        title={selectedTicket ? `Ticket: ${selectedTicket.subject}` : 'Ticket'}
        open={ticketModalVisible}
        onCancel={() => {
          ticketCleanupRef.current?.()
          ticketCleanupRef.current = null
          setTicketModalVisible(false)
          setSelectedTicket(null)
        }}
        footer={null}
        width={920}
      >
        {selectedTicket ? (
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Card>
              <Space wrap>
                <Tag color={statusColors[selectedTicket.status] ?? 'default'}>{selectedTicket.status}</Tag>
                <Tag color={priorityColors[selectedTicket.priority] ?? 'default'}>{selectedTicket.priority}</Tag>
                <Tag>{selectedTicket.topic_label || 'Other'}</Tag>
                <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
              </Space>
              {selectedTicket.auto_priority_reason ? (
                <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                  {selectedTicket.auto_priority_reason}
                </Typography.Paragraph>
              ) : null}
            </Card>

            {selectedTicket.auto_reply_message ? (
              <Alert
                type="success"
                showIcon
                title="Auto acknowledgement sent"
                description={selectedTicket.auto_reply_message}
              />
            ) : null}

            <Card title="Conversation">
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                {(selectedTicket.messages ?? []).map((ticketMessage) => (
                  <div
                    key={ticketMessage.id}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: ticketMessage.author_type === 'admin' || ticketMessage.author_type === 'system' ? '#f8fafc' : '#e6f4ff',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <strong>{ticketMessage.author_name || (ticketMessage.is_admin ? 'Admin' : 'Seller')}</strong>
                      <span style={{ color: '#64748b', fontSize: 12 }}>{new Date(ticketMessage.created_at).toLocaleString()}</span>
                    </div>
                    <div>{ticketMessage.message}</div>
                  </div>
                ))}
              </Space>
            </Card>

            {selectedTicket.status !== 'closed' ? (
              <Card title="Reply">
                <Form form={replyForm} layout="vertical" onFinish={replyToTicket}>
                  {canReplyTicket ? (
                    <Form.Item name="message" rules={[{ required: true, message: 'Reply message is required' }]}>
                      <Input.TextArea rows={4} placeholder="Write your reply" />
                    </Form.Item>
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      title="Read-only ticket access"
                      description="You can review this ticket, but you do not have permission to reply."
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    {canCloseTicket ? (
                      <Button danger onClick={() => void closeTicket()} loading={saving}>
                        Close Ticket
                      </Button>
                    ) : <span />}
                    {canReplyTicket ? (
                      <Button type="primary" htmlType="submit" loading={saving}>
                        Send Reply
                      </Button>
                    ) : null}
                  </div>
                </Form>
              </Card>
            ) : (
              <Alert type="warning" showIcon title="This ticket is closed" description="You can create a fresh ticket if the issue comes back or if you need help with a new problem." />
            )}
          </Space>
        ) : null}
      </Modal>
    </div>
  )
}

export default SupportPage
